import { useCallback, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useClientContext } from 'client/context';
import { selectModal } from 'ducks/modals/selectors';
import { clearModal } from 'ducks/modals/slices';
import { selectPair } from 'ducks/pair/selectors';
import { updateSettings } from 'ducks/settings/slices';
import { Button } from 'framework/button';
import { DropdownCalendar } from 'framework/dropdown';
import { InputPercent } from 'framework/Input';
import { Modal } from 'framework/modal';
import moment from 'moment';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { mergeQuery } from 'utils/queries';
import { uuidv4 } from 'utils/uuid';

import { form_default } from './constants';
import { CandleValidationsForm } from './types';

import cn from '../Modal.module.scss';

export const CandleValidationsModal = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const voladeum_pair = useAppSelector(selectPair);
  const { is_open } = useAppSelector(selectModal('candle_validations'));
  const [form, setForm] = useState<CandleValidationsForm>(form_default);
  const { Voladeum, handleError } = useClientContext();

  const onClose = useCallback(() => {
    dispatch(clearModal('candle_validations'));
  }, [dispatch]);

  const onSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      try {
        e.preventDefault();
        dispatch(updateSettings({ loading: true }));
        const { since_date, accuracy_threshold } = form;
        const since_milliseconds = moment(since_date).utc().unix() * 1000;
        const payload = { accuracy_threshold };
        await Voladeum.candleValidations.reset(voladeum_pair.pair_id, since_milliseconds, payload);

        onClose();
        navigate(`${pathname}${mergeQuery(search, { update: uuidv4() })}`);
      } catch (err) {
        handleError(err);
      }
    },
    [Voladeum, handleError, voladeum_pair, pathname, search, navigate, dispatch, onClose, form]
  );

  const updateForm = useCallback((item: Partial<CandleValidationsForm>) => {
    setForm((prev) => ({ ...prev, ...item }));
  }, []);

  const { since_date, accuracy_threshold_formatted } = form;

  return (
    <Modal
      is_open={is_open}
      identifier="confirm"
      width="400px"
      onSubmit={onSubmit}
      onClose={onClose}
    >
      <Modal.Header onClose={onClose} dismiss>
        Reset Validations
      </Modal.Header>
      <Modal.Body>
        <DropdownCalendar
          label="Since Date"
          value={[since_date]}
          onSelect={([value]) => updateForm({ since_date: value })}
        />
        <InputPercent
          label="CandleValidation Threshold"
          information="Skip validations above accuracy threshold"
          value={accuracy_threshold_formatted}
        />
      </Modal.Body>
      <Modal.Footer>
        <div className={cn.flex} />
        <Button type="submit">Ok</Button>
      </Modal.Footer>
    </Modal>
  );
};
